import classNames from 'classnames';
import { ChevronRight } from 'react-feather';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { setServerErrors, useMutation } from '../../../Shared/api';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { ProjectType } from '../../../Shared/types';
import { Button } from '../../../Shared/UI/Button';
import ErrorMessage from '../../../Shared/UI/ErrorMessage';
import { DonationBox, Icon, Megaphone } from '../../../Shared/UI/Icon';
import { Input } from '../../../Shared/UI/Input';
import InputGroup from '../../../Shared/UI/InputGroup';
import InputLabel from '../../../Shared/UI/InputLabel';
import { Radio } from '../../../Shared/UI/Radio';
import RadioLabel from '../../../Shared/UI/RadioLabel';
import ServerErrors from '../../../Shared/UI/ServerErrors';
import { useRouter } from '../../router';

export default function AddProjectForm({
  onSuccess,
}: {
  onSuccess?: () => void;
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const form = useForm({
    defaultValues: {
      title: '',
      type: ProjectType.Fundraising,
      enabled: false,
    },
  });

  const { register, handleSubmit, setError, formState: { errors, defaultValues } } = form;

  const [addProject, { loading, errors: serverErrors }] = useMutation(routes.add_project(), {
    onSuccess: () => {
      notifier.notify(t('backend:project_created'));
      onSuccess?.();
    },
    onError: (errors) => {
      setServerErrors(errors, setError, defaultValues);
    },
  });

  return (
    <form onSubmit={handleSubmit(addProject)}>
      <div className="space-y-6">
        <div className="space-y-2">
          <h3 className="py-1">
            {t('backend:add_a_project')}
          </h3>
          <p className="text-slate-700">
            {t('backend:add_project_intro')}
          </p>
        </div>

        <hr />

        {serverErrors && <ServerErrors errors={serverErrors} defaultValues={defaultValues} />}

        <InputGroup>
          <InputLabel htmlFor="select_project_type" required valid={!errors.type}>
            {t('backend:project_type')}
          </InputLabel>
          <RadioLabel
            className={classNames(
              'relative p-3 overflow-hidden bg-white border rounded-md border-slate-250 hover:border-slate-300',
              'z-0', // Safari overflow fix
            )}
          >
            <Radio
              {...form.register('type', { required: true })}
              value={ProjectType.Fundraising}
              aria-invalid={!!errors.type}
              id="project_type"
            />
            <div className="leading-snug pr-[80px] font-semibold py-0.5">
              {t('backend:project_types.fundraising')}
            </div>
            <div className="rounded-l-full w-[100px] h-[160px] bg-emerald-50 text-emerald-600 flex items-center justify-center absolute -right-6 pr-5 top-1/2 -mt-[80px] text-2xl">
              <Icon>
                <Megaphone strokeWidth={1.5} />
              </Icon>
            </div>
          </RadioLabel>
          <RadioLabel
            className={classNames(
              'relative p-3 overflow-hidden bg-white border rounded-md border-slate-250 hover:border-slate-300 mt-2',
              'z-0', // Safari overflow fix
            )}
          >
            <Radio
              {...form.register('type', { required: true })}
              value={ProjectType.DonationBox}
              aria-invalid={!!errors.type}
            />
            <div className="leading-snug pr-[80px] font-semibold py-0.5">
              {t('backend:project_types.donation_box')}
            </div>
            <div className="rounded-l-full w-[100px] h-[160px] bg-emerald-50 text-emerald-600 flex items-center justify-center absolute -right-6 pr-5 top-1/2 -mt-[80px] text-2xl">
              <Icon>
                <DonationBox strokeWidth={1.5} />
              </Icon>
            </div>
          </RadioLabel>
          <ErrorMessage attribute={t('backend:project_type')} error={errors.type} />
        </InputGroup>

        <InputGroup>
          <InputLabel required valid={!errors.title} htmlFor="create_project_title">
            {t('backend:project_title')}
          </InputLabel>
          <Input
            {...register('title', { required: true })}
            id="create_project_title"
            className="mr-4"
            aria-invalid={!!errors.title}
          />
          <ErrorMessage attribute={t('backend:project_title')} error={errors.title }/>
        </InputGroup>

        <hr />

        <div className="text-right">
          <Button
            type="submit"
            disabled={loading}
            loading={loading}
          >
            {t('backend:continue')}
            <Icon className="ml-1">
              <ChevronRight />
            </Icon>
          </Button>
        </div>
      </div>
    </form>
  );
}
